import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiTrash2, FiMessageCircle, FiPhone, FiMail, FiUser, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { contactAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 15;

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await contactAPI.getAll();
        const data = response.data?.data?.contacts || [];
        setContacts(data);
      } catch {
        toast.error('Failed to load contact messages.');
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    setFiltered(!q ? contacts : contacts.filter((c) =>
      c.name?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.message?.toLowerCase().includes(q)
    ));
    setPage(1);
  }, [search, contacts]);

  const handleWhatsApp = async (contact) => {
    if (!contact.isRead) {
      try {
        await contactAPI.markAsRead(contact._id);
        setContacts((prev) => prev.map((c) => c._id === contact._id ? { ...c, isRead: true } : c));
      } catch {}
    }
    const cleaned = contact.phone.replace(/[\s\-().]/g, '');
    window.open(`https://wa.me/${cleaned}`, '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    setDeletingId(id);
    try {
      await contactAPI.delete(id);
      setContacts((prev) => prev.filter((c) => c._id !== id));
      toast.success('Message deleted.');
    } catch {
      toast.error('Failed to delete message.');
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const unreadCount = contacts.filter((c) => !c.isRead).length;

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Contact Messages</h2>
          <p className="text-slate-500 text-sm mt-1">Messages submitted through the website contact form</p>
        </div>
        {unreadCount > 0 && (
          <span className="px-3 py-1.5 bg-sky-100 text-sky-700 border border-sky-200 rounded-full text-sm font-semibold">
            {unreadCount} unread
          </span>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-4 items-center shadow-sm">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search by name, phone, email or message..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
        <div className="text-sm text-slate-400 flex-shrink-0">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Phone</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Message</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((contact, idx) => (
                  <motion.tr key={contact._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.04 }}
                    className={`border-b border-blue-50 hover:bg-blue-50/30 transition-colors ${!contact.isRead ? 'bg-sky-50/40' : ''}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {!contact.isRead && (
                          <span className="w-2 h-2 bg-sky-400 rounded-full flex-shrink-0" title="Unread" />
                        )}
                        <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                          <FiUser className="w-4 h-4 text-slate-500" />
                        </div>
                        <span className="font-semibold text-[#1e3a8a] text-sm">{contact.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-slate-600 text-sm">
                        <FiPhone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        {contact.phone}
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-500 text-sm">
                      {contact.email ? (
                        <div className="flex items-center gap-1.5">
                          <FiMail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          {contact.email}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-slate-500 text-sm max-w-xs">
                      <p className="truncate">{contact.message}</p>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">
                      {formatDate(contact.createdAt)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleWhatsApp(contact)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-semibold transition-colors"
                          title={`WhatsApp ${contact.phone}`}>
                          <FiMessageCircle className="w-3.5 h-3.5" />
                          Contact
                        </button>
                        <button onClick={() => handleDelete(contact._id)} disabled={deletingId === contact._id}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Delete">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginated.length === 0 && !loading && (
            <div className="text-center py-16">
              <FiMessageCircle className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No contact messages yet</p>
              <p className="text-slate-400 text-sm mt-1">Messages submitted through the contact form will appear here.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-blue-50">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                <FiChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                <FiChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContacts;
